import type { FirmContactView } from "../../../../interfaces/models/IFirmContact";
import { getJurisdictionLabel } from "../../../../utils/jurisdiction/structuredJurisdiction";
import { firmContactBlockKey } from "../../../../utils/firmContactUtils";
import {
  FIRM_CONTACT_SLOTS,
  FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS,
  getAssignedMultiContacts,
  isFirmContactAssigned,
} from "../../../../utils/firmContactRoleConfig";

export type FirmContactRoleAction = "keep" | "assign";

export type FirmContactRoleAssignment = {
  action: FirmContactRoleAction;
  /** Required when assigning to a multi role that is already at max capacity. */
  removeContactId?: string;
};

export type FirmContactAssignmentsMap = Record<string, FirmContactRoleAssignment>;

const EMPTY_CONTACT: FirmContactView["primaryContact"] = {
  id: "",
  fullName: "",
  email: "",
  phone: { code: "", phone: "", basecode: "" },
};

export function buildFirmContactAssignmentKey(
  blockKey: string,
  roleKey: string,
): string {
  return `${blockKey}::${roleKey}`;
}

export function createEmptyFirmContactAssignments(): FirmContactAssignmentsMap {
  return {};
}

export function hasFirmContactAssignmentChanges(
  assignments: FirmContactAssignmentsMap,
): boolean {
  return Object.values(assignments).some(
    (assignment) => assignment.action === "assign",
  );
}

function contactFromPerson(
  personId: string,
  fullName: string,
  email: string,
): FirmContactView["primaryContact"] {
  return {
    id: personId,
    fullName,
    email,
    phone: { ...EMPTY_CONTACT.phone },
  };
}

function personAlreadyInMultiRole(
  contacts: FirmContactView["primaryContact"][] | undefined,
  personId: string,
): boolean {
  const normalizedPersonId = String(personId ?? "").trim();
  if (!normalizedPersonId) {
    return false;
  }
  return getAssignedMultiContacts(contacts).some(
    (contact) => String(contact.id ?? "").trim() === normalizedPersonId,
  );
}

function applySingleRoleAssignment(
  contact: FirmContactView,
  field:
    | "primaryContact"
    | "secondaryContact"
    | "accountingContact"
    | "itContact"
    | "socialMediaContact"
    | "hrContact"
    | "ccContact",
  action: FirmContactRoleAction,
  personId: string,
  personFullName: string,
  personEmail: string,
): FirmContactView {
  if (action === "keep") {
    return contact;
  }

  return {
    ...contact,
    [field]: contactFromPerson(personId, personFullName, personEmail),
  };
}

function applyMultiRoleAssignment(
  contact: FirmContactView,
  field: "marketingContacts" | "liaisonContacts",
  action: FirmContactRoleAction,
  personId: string,
  personFullName: string,
  personEmail: string,
  removeContactId?: string,
): FirmContactView {
  if (action === "keep") {
    return contact;
  }

  const current = getAssignedMultiContacts(contact[field]);
  if (personAlreadyInMultiRole(contact[field], personId)) {
    return contact;
  }

  const slot = FIRM_CONTACT_SLOTS.find((item) => item.field === field);
  const max =
    slot?.cardinality === "multi" ? slot.maxSelections : 5;

  if (current.length < max) {
    return {
      ...contact,
      [field]: [
        ...current,
        contactFromPerson(personId, personFullName, personEmail),
      ],
    };
  }

  const removeId = String(removeContactId ?? "").trim();
  if (!removeId) {
    return contact;
  }

  const withoutRemoved = current.filter(
    (item) => String(item.id ?? "").trim() !== removeId,
  );
  return {
    ...contact,
    [field]: [
      ...withoutRemoved,
      contactFromPerson(personId, personFullName, personEmail),
    ],
  };
}

export function applyFirmContactAssignmentsToBlock(
  contact: FirmContactView,
  assignments: FirmContactAssignmentsMap,
  params: {
    personId: string;
    personFullName: string;
    personEmail: string;
  },
): FirmContactView {
  const blockKey = firmContactBlockKey(contact);
  let next = contact;

  for (const slot of FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS) {
    const assignment =
      assignments[buildFirmContactAssignmentKey(blockKey, slot.key)];
    if (!assignment || assignment.action === "keep") {
      continue;
    }

    if (slot.cardinality === "single") {
      next = applySingleRoleAssignment(
        next,
        slot.field,
        assignment.action,
        params.personId,
        params.personFullName,
        params.personEmail,
      );
      continue;
    }

    next = applyMultiRoleAssignment(
      next,
      slot.field,
      assignment.action,
      params.personId,
      params.personFullName,
      params.personEmail,
      assignment.removeContactId,
    );
  }

  return next;
}

export function validateFirmContactAssignments(
  assignments: FirmContactAssignmentsMap,
  contacts: FirmContactView[],
  params: {
    personId?: string;
    personFullName: string;
    isAttorney?: boolean;
  },
): string | null {
  const personId = String(params.personId ?? "").trim();
  const hasAssign = hasFirmContactAssignmentChanges(assignments);
  if (!hasAssign) {
    return null;
  }

  if (!personId && !params.personFullName.trim()) {
    return "Enter the person's name before assigning firm contact roles.";
  }

  for (const contact of contacts) {
    const blockKey = firmContactBlockKey(contact);

    for (const slot of FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS) {
      const assignment =
        assignments[buildFirmContactAssignmentKey(blockKey, slot.key)];
      if (!assignment || assignment.action === "keep") {
        continue;
      }

      if (
        slot.contactType === "primaryContact" ||
        slot.contactType === "secondaryContact"
      ) {
        if (!params.isAttorney) {
          return `${slot.title} can only be assigned to attorneys.`;
        }
      }

      if (slot.cardinality === "multi" && personId) {
        if (personAlreadyInMultiRole(contact[slot.field], personId)) {
          continue;
        }

        const assigned = getAssignedMultiContacts(contact[slot.field]);
        if (
          assigned.length >= slot.maxSelections &&
          !String(assignment.removeContactId ?? "").trim()
        ) {
          return `Select a contact to remove before assigning ${slot.title} for ${getJurisdictionLabel({ jurisdiction: contact.jurisdiction }) || "this office"}.`;
        }
      }
    }
  }

  return null;
}

export function isPersonAssignedToSingleRole(
  contact: FirmContactView["primaryContact"] | undefined,
  personId: string,
): boolean {
  const normalizedPersonId = String(personId ?? "").trim();
  if (!normalizedPersonId || !isFirmContactAssigned(contact)) {
    return false;
  }
  return String(contact?.id ?? "").trim() === normalizedPersonId;
}
